// Loads a picker's options once and exposes an error + a retry (Session AEH, FIX-2 B3), so a
// failed load is never a silent empty dropdown (the AEE "silent-empty required picker" class:
// the office types into a required select that is empty only because its options fetch failed,
// with nothing on screen to say so or to try again).
//
// The hook keeps the last-known data on a later failure (a transient blip does not blank a picker
// that already loaded) and surfaces `error` so the caller can render an inline "Could not load"
// + Retry next to the field. `retry()` re-runs the loader.
import { useCallback, useEffect, useState } from "react";

export interface OptionsLoaderState<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

export function useOptionsLoader<T>(loader: () => Promise<T>, deps: unknown[] = []): OptionsLoaderState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [nonce, setNonce] = useState(0);

  // The loader identity is intentionally keyed on the caller-supplied deps (not the function
  // reference, which a caller usually recreates every render) plus the retry nonce.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(loader, deps);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    run()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [run, nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, retry };
}
